/**
 * Mapy.cz REST API (developer.mapy.com) – volání přímo z prohlížeče dle dokumentace.
 * Geocoding, reverse geocoding, suggest. API klíč se předává v požadavku (query apikey + hlavička X-Mapy-Api-Key).
 * V portálu developer.mapy.com je u klíče potřeba: zapnout službu Geocoding a nastavit HTTP referer (viz README).
 */
import axios from 'axios';

const BASE_URL = 'https://api.mapy.cz/v1';

/**
 * Geocoding – vyhledání adresy/místa dle textu.
 * @param {string} query – hledaný text
 * @param {string} apiKey – API klíč z konfigurace (CompanyConfiguration.MapyApiKey)
 * @param {object} options – např. { limit: 10, locality: 'BOX(12,48,19,51)' } pro ČR
 * @returns {Promise<Array>} – pole položek { name, location, position, coords }
 */
export async function geocode(query, apiKey, options = {}) {
    const key = (apiKey || '').trim();
    if (!key || !query || !query.trim()) return [];
    try {
        const params = { query: query.trim(), lang: 'cs', limit: options.limit || 10 };
        if (options.locality) params.locality = options.locality;
        const res = await axios.get(`${BASE_URL}/geocode`, {
            params: { ...params, apikey: key },
            headers: { 'X-Mapy-Api-Key': key },
            timeout: 10000
        });
        const items = res.data?.items || [];
        return items.map((item) => ({
            name: item.name,
            location: item.location,
            position: item.position,
            coords: item.position ? { y: item.position.lat, x: item.position.lon } : null
        }));
    } catch (e) {
        console.warn('Mapy.cz geocode error:', e?.response?.status, e?.message);
        return [];
    }
}

/**
 * Reverse geocoding – z souřadnic vrátí adresu/místo.
 * @param {number} lon – délka
 * @param {number} lat – šířka
 * @param {string} apiKey – API klíč
 * @returns {Promise<object|null>} – { items: [...] } nebo null
 */
export async function rgeocode(lon, lat, apiKey) {
    const key = (apiKey || '').trim();
    if (!key) return null;
    try {
        const res = await axios.get(`${BASE_URL}/rgeocode`, {
            params: { lon, lat, lang: 'cs', apikey: key },
            headers: { 'X-Mapy-Api-Key': key },
            timeout: 10000
        });
        return res.data;
    } catch (e) {
        console.warn('Mapy.cz rgeocode error:', e?.response?.status, e?.message);
        return null;
    }
}

/**
 * Suggest – návrhy při psaní (autocomplete).
 * @param {string} query
 * @param {string} apiKey
 * @param {object} options – např. { limit: 8, locality: 'BOX(12,48,19,51)' }
 * @returns {Promise<Array>} – stejný formát jako geocode + title, secondRow
 */
export async function suggest(query, apiKey, options = {}) {
    const key = (apiKey || '').trim();
    if (!key) return [];
    if (!query || !query.trim()) return [];
    try {
        const params = { query: query.trim(), lang: 'cs', limit: options.limit || 8 };
        if (options.locality) params.locality = options.locality;
        const res = await axios.get(`${BASE_URL}/suggest`, {
            params: { ...params, apikey: key },
            headers: { 'X-Mapy-Api-Key': key },
            timeout: 8000
        });
        const items = res.data?.items || [];
        return items.map((item) => ({
            name: item.name,
            location: item.location,
            position: item.position,
            coords: item.position ? { y: item.position.lat, x: item.position.lon } : null,
            title: item.name,
            secondRow: item.location || ''
        }));
    } catch (e) {
        console.warn('Mapy.cz suggest error:', e?.response?.status, e?.message);
        return [];
    }
}

export const mapyApiService = { geocode, rgeocode, suggest };
